'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { User, Check, Prohibit } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { Validator } from '@/utils/abacus'
import { updateProfileField } from '@/services/userService'

interface ProfileCompletionModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: (fullName: string) => void
  onCancel?: () => void
  title?: string
  description?: string
}

export function ProfileCompletionModal({
  isOpen,
  onClose,
  onComplete,
  onCancel,
  title = 'Profilini Tamamla',
  description = 'Apple veya sosyal medya ile giriş yaptığınız için adınız tanımlanamadı. Devam edebilmek için lütfen adınızı ve soyadınızı belirtin.'
}: ProfileCompletionModalProps) {
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Oturum bulunamadı')

      const sanitizedName = Validator.sanitize(fullName)
      const nameCheck = Validator.fullName(sanitizedName)
      if (!nameCheck.isValid) {
        throw new Error(nameCheck.error || 'Geçersiz isim')
      }

      // public.users güncelle
      const { error: updateError } = await updateProfileField(
        user.id,
        'full_name',
        nameCheck.normalized
      )

      if (updateError) throw new Error(updateError)

      onComplete(nameCheck.normalized)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) onCancel()
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCancel}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-md bg-white rounded-[32px] p-8 border border-amber-100 shadow-2xl overflow-hidden font-sans"
          >
            {/* Top Light Effect */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-amber-500/10 via-amber-500/5 to-transparent blur-2xl" />

            <div className="relative z-10 space-y-6">
              {/* Header Icon */}
              <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center mx-auto shadow-sm">
                <User size={32} weight="duotone" className="text-[#D4AF37]" />
              </div>

              {/* Title & Description */}
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                  {title}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed max-w-[280px] mx-auto">
                  {description}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                    Adınız ve Soyadınız
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Ahmet Yılmaz"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full h-13 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/10 outline-none transition-all font-medium text-sm"
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="text-red-500 text-xs font-bold text-center bg-red-50 p-3 rounded-xl border border-red-100"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  {onCancel && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex-1 h-13 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-1.5 active:scale-[0.98]"
                    >
                      <Prohibit size={18} weight="bold" />
                      Vazgeç
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading || fullName.trim().length < 3}
                    className="flex-1 h-13 bg-gradient-to-r from-amber-500 to-[#D4AF37] text-white font-bold rounded-xl text-sm shadow-lg shadow-amber-500/20 hover:opacity-95 transition-opacity flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98]"
                  >
                    {loading ? (
                      'Kaydediliyor...'
                    ) : (
                      <>
                        <Check size={18} weight="bold" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
